import { select, all, call, put, race, take, takeEvery, takeLatest } from 'redux-saga/effects'
import { push } from 'connected-react-router'
import { delay } from 'redux-saga/effects'
import agent, { mocked } from 'agent'
import appMode from 'helpers/getAppMode'
import { removeHTTPProtocol } from 'helpers/util'

import * as actions from '../actions/keywordFinder.actions'
import * as searchActions from '../actions/search.actions'
import * as appUsageActions from '../actions/appUsage.actions'
import { isDomainValid } from 'helpers/validate'

function* pollKeywordFinderSerpSaga() {
  try {
    const state = yield select()
    const { value, location, language } = state.search
    let endpoint = agent.KeywordFinder.get
    if (appMode.preview) {
      endpoint = mocked.KeywordFinder.get
    }
    const payload = {
      keywordFinder: {
        keyword: value,
        location: location,
        language: language,
        type: 'Suggestion'
      }
    }
    const body = yield call(endpoint, payload)
    if (body) {
      const data = body.data
      if (data.attributes.status === 'complete') {
        yield put(actions.getKeywordFinderSuggestionsSuccess(data))
      } else if (data.attributes.results.length > 0) {
        yield put(actions.getKeywordFinderSuggestionsPartialSuccess(data))
      }
    }

    yield delay(5000)
  } catch (error) {
    yield put(actions.getKeywordFinderSuggestionsFailure(error))
  }
}

function* pollKeywordFinderSerpDomainSaga() {
  try {
    const state = yield select()
    const { value, location, language } = state.search
  
    const url = removeHTTPProtocol(value)
    const payload = {
      keywordFinder: {
        url: url,
        location: location,
        language: language
      }
    }
    const body = yield call(agent.KeywordFinder.getSuggestions, payload)
    if (body) {
      const data = body.data
      yield put(actions.getKeywordFinderDomainSuccess(data))
    }
  } catch (error) {
    yield put(actions.getKeywordFinderDomainFailure(error))
  }
}

function* loadPreviousSaga(params) {
  try {
    const { historyId } = params.payload
    yield put(push(`/keyword-finder/history/${historyId}`))
    const body = yield call(agent.KeywordFinder.getPrevious, historyId)
    yield put(actions.loadPreviousSuccess(body.data))
  } catch (error) {
    yield put(actions.loadPreviousFailure(error))
  }
}

function* getKeywordFinderKDSaga(params) {
  const { keywordId, keywordInfoId } = params.payload
  let complete = false
  // We only allow 20 checks otherwise fail
  let strikes = 20
  while (strikes > 0) {
    try {
      const body = yield call(agent.KeywordFinder.getSerp, keywordId, keywordInfoId)

      strikes -= 1

      complete = body.data.attributes.status === 'complete'

      if (complete) {
        const difficulty = body.data.attributes.keywordDifficulty
        const results = body.data.attributes.results
        const keywordId = body.data.id

        yield put(actions.getKeywordFinderKDSuccess(results, keywordId, false, difficulty))
        return
      }
    } catch (error) {
      yield put(actions.getKeywordFinderKDFailure(error, keywordInfoId, keywordId))
      if(error.status === 422) return
    }
    yield delay(5000)
  }
}
function* getKeywordFinderSERPSaga(params) {
  const { keywordId, keywordInfoId } = params.payload
  let complete = false
  // We only allow 20 checks otherwise fail
  let strikes = 20
  while (strikes > 0) {
    try {
      const body = yield call(agent.KeywordFinder.getSerp, keywordId, keywordInfoId)

      strikes -= 1

      complete = body.data.attributes.status === 'complete'

      if (complete) {
        const results = body.data.attributes.results
        const keywordId = body.data.id

        yield put(actions.getKeywordFinderSERPSuccess(results, keywordId))
        return
      }
    } catch (error) {
      yield put(actions.getKeywordFinderSERPFailure(error, keywordInfoId, keywordId))
      if(error.status === 422) return
    }
    yield delay(5000)
  }
}

function* getKeywordFinderHistorySaga() {
  try {
    const body = yield call(agent.KeywordFinder.getHistory)
    yield put(actions.getKeywordFinderHistorySuccess(body.data))
  } catch (error) {
    yield put(actions.getKeywordFinderHistoryFailure(error))
  }
}

function* getKeywordSuggestionsByDomainSaga(params) {
  try {
    const { domainData } = params.payload
    const body = yield call(agent.KeywordFinder.getSuggestions, domainData)
    yield put(actions.getKeywordSuggestionsByDomainSuccess(body.data))
  } catch (error) {
    yield put(actions.getKeywordSuggestionsByDomainFailure(error))
  }
}

function* pollKeywordFinderResults() {
  const state = yield select()
  const { type } = state.search

  switch (true) {
    case type === 'Domain':
      yield race([
        call(pollKeywordFinderSerpDomainSaga),
        take(actions.getKeywordFinderDomainSuccess.type),
        take(actions.getKeywordFinderDomainFailure.type)
      ])
      break
    default:
      yield race([
        call(pollKeywordFinderSerpSaga),
        take(actions.getKeywordFinderSuggestionsSuccess.type),
        take(actions.getKeywordFinderSuggestionsFailure.type),
        take(actions.getKeywordFinderSuggestionsNotReady.type)
      ])
  }
}

function* validateKeywordFinderSearch() {
  const state = yield select()
  const { used, limit } = state.appUsage.keywordFinder
  const { type, value } = state.search

  if (limit && used && used >= limit) {
    yield put(searchActions.setError('Used monthly limit. Upgrade plan or try again later.'))
    return null
  }
  if (type === 'Domain' && !isDomainValid(value)) {
    yield put(searchActions.setError('Please enter a proper domain value'))
    return null
  }
  if (!value) {
    yield put(searchActions.setError('Please enter a keyword'))
    return null
  }

  yield put(appUsageActions.hitKeywordFinder(1))
  yield put(actions.getKeywordFinderResults())
}

function* deleteKeywordsSaga(params) {
  try {
    const { historyId } = params.payload
    const data = yield call(agent.KeywordFinder.del, historyId)
    yield put(actions.deleteKeywordsSuccess(data))
  } catch (error) {
    yield put(actions.deleteKeywordsFailure(error))
  }
}

export default function* keywordFinderSagas() {
  yield all([
    takeLatest(actions.getResults.type, validateKeywordFinderSearch),
    takeLatest(actions.getKeywordFinderResults.type, pollKeywordFinderResults),
    takeLatest(actions.loadPrevious.type, loadPreviousSaga),
    takeLatest(actions.getKeywordSuggestionsByDomain.type, getKeywordSuggestionsByDomainSaga),
    takeLatest(actions.getKeywordFinderHistory.type, getKeywordFinderHistorySaga),
    takeEvery(actions.getKeywordFinderKD.type, getKeywordFinderKDSaga),
    takeEvery(actions.getKeywordFinderSERP.type, getKeywordFinderSERPSaga),
    takeLatest(actions.deleteKeywords.type, deleteKeywordsSaga)
  ])
}
